import {
  Button,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spacer,
  Text,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react'
import { FC, MouseEventHandler } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { WhiteLogo } from './Logo'
import { UserSummaryPanel } from '../UserSummaryPanel'
import { LocationSwitcher } from './LocationSwitcher'
import { DESKTOP_BREAKPOINT } from '../../constants'
import { useLogout } from '../../hooks/useLogout'
import { useAppSelector } from '../../app/typedReduxHooks'
import { Permission } from '../../types/permission'
import { ExternalLinkIcon } from '@chakra-ui/icons'
import { useHasEntitlement } from '../../pages/OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types'
import { useHasPermission } from '../../hooks/useHasPermission'
import { routes } from '../../pages/routes'
import { AnalyticsAction, analyticsAction } from '../../common/utils/analytics'

interface SideNavDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface NavProps {
  isSmallScreenNavOpen: boolean
  onCloseSmallScreenNav: () => void
}

interface SideNavLinkProps {
  to: string
  title: string
  action: AnalyticsAction
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const SideNavLink: FC<SideNavLinkProps> = ({ to, title, action, onClick }) => {
  const location = useLocation()
  const linkPath = to.split('?')[0]
  const isActive = location.pathname === linkPath

  return (
    <Button
      justifyContent="flex-start"
      colorScheme="blue"
      bgColor={isActive ? 'blue.200' : 'blue.800'}
      as={Link}
      to={to}
      onClick={onClick}
      {...analyticsAction(action)}
    >
      {title}
    </Button>
  )
}

const SideNav: FC<{ isNavDrawer?: boolean }> = ({ isNavDrawer }) => {
  const logout = useLogout()
  const permissions = useAppSelector((state) => state.authentication.permissions)
  const prescriber = useAppSelector((state) => state.prescriber?.prescriber)

  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isEnableHelpDocument = organizationSettings?.enableHelpDocument
  const hasMultipleLocationsFeatureOn = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const hasDataExportManagement = useHasPermission(Permission.DATA_EXPORT_MANAGEMENT, {
    includeOwnerSuperAdminFallback: true,
  })

  return (
    <Flex flexDirection="column" bgColor="blue.800" p="3" h="100%">
      <VStack p={'6'}>
        <Center>
          <WhiteLogo maxWidth={150} />
        </Center>
      </VStack>
      {
        <VStack spacing={10} justifyContent="space-between">
          <VStack width={isNavDrawer ? '296px' : '260px'} spacing={2} alignItems="stretch">
            {prescriber && hasMultipleLocationsFeatureOn && <LocationSwitcher />}
            {permissions?.includes(Permission.PRESCRIBING) && (
              <SideNavLink to="/new-script" title="New Prescription" action="nav_new_script" />
            )}
            {(prescriber || permissions?.includes(Permission.PATIENT_MANAGEMENT)) && (
              <SideNavLink to="/patients" title="Manage Patients" action="nav_patients" />
            )}
            {(prescriber || permissions?.includes(Permission.PRESCRIPTION_MANAGEMENT)) && (
              <SideNavLink
                to="/prescriptions"
                title="Manage Prescriptions"
                action="nav_prescriptions"
              />
            )}
          </VStack>
        </VStack>
      }
      <Spacer />
      <VStack width="260px" spacing={10} alignItems="stretch" justifyContent="space-between">
        <VStack width="260px" spacing={2} alignItems="stretch">
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="whiteAlpha.600"
            mb={2}
            letterSpacing="wider"
            pt={4}
          >
            SUPPORT
          </Text>

          {permissions && permissions.length > 0 && (
            <SideNavLink to="/settings" title="Settings" action="nav_settings" />
          )}
          {hasDataExportManagement ? (
            <SideNavLink
              to={`${routes.dataReports.path}?tab=exports`}
              title="Data Reports"
              action="nav_data_reports"
            />
          ) : null}
          {isEnableHelpDocument ? (
            <Button
              justifyContent="flex-start"
              colorScheme="blue"
              bgColor={'blue.800'}
              as="a"
              href="https://help.rxpad.com.au"
              target="_blank"
              rightIcon={<ExternalLinkIcon />}
              {...analyticsAction('nav_help')}
            >
              Help
            </Button>
          ) : null}

          <SideNavLink
            to="/"
            title="Logout"
            action="sign_out"
            onClick={(e) => {
              e.preventDefault()
              logout()
            }}
          />
        </VStack>
        <UserSummaryPanel />
      </VStack>
    </Flex>
  )
}

export const SideNavDrawer: FC<Required<SideNavDrawerProps>> = ({ isOpen, onClose }) => (
  <Drawer isOpen={isOpen} placement="left" onClose={onClose} size={'xs'}>
    <DrawerOverlay />
    <DrawerContent>
      <DrawerCloseButton color="white" />
      <SideNav isNavDrawer />
    </DrawerContent>
  </Drawer>
)

const Nav: FC<NavProps> = ({ isSmallScreenNavOpen, onCloseSmallScreenNav }) => {
  const [isDesktop] = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT})`)
  return isDesktop ? (
    <SideNav />
  ) : (
    <SideNavDrawer isOpen={isSmallScreenNavOpen} onClose={onCloseSmallScreenNav} />
  )
}

export default Nav
